import { useEffect } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { useSiteInit } from '@autopass-web/domain';
import FeatureMaintenance from '@autopass-web/feature-maintenance';

import { PATH } from './config';

import './utils/version-log';

import { AuthPage } from '#/guest-aps/pages/auth-page';
import { ErrorPage } from '#/guest-aps/pages/error-page';
import { LeavePage } from '#/guest-aps/pages/leave-page';
import { PaymentPage } from '#/guest-aps/pages/payment-page';
import { PoiEntryPage } from '#/guest-aps/pages/poi-entry-page';
import { PromotionPage } from '#/guest-aps/pages/promotion-page';
import { SearchPlateNumberPage } from '#/guest-aps/pages/search-plate-number-page';
import { SelectPlateNumberPage } from '#/guest-aps/pages/select-plate-number-page';
import { SerialCodeEntryPage } from '#/guest-aps/pages/serial-code-entry-page';
import { SuccessPage } from '#/guest-aps/pages/success-page';
import amplitude from '#/guest-aps/services/amplitude';
import { sessionStorageController } from '#/guest-aps/services/storages';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const App = () => {
  useSiteInit();

  useEffect(() => {
    const parkingBill = sessionStorageController.getSelectedParkingBill();
    const plateNumber = parkingBill?.plate_number;

    amplitude.init(plateNumber);
    // http scheme force redirect to https
    const { protocol, host, hostname, pathname, search } = window.location;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    if (protocol !== 'https:' && !isLocalhost) {
      window.location.replace(`https://${host}${pathname}${search}`);
    }
  }, []);

  // // Sentry.ErrorBoundary does not catch errors happening inside a event handler
  // // use "unhandledrejection" event to catch those errors and navigate users to Error Page
  useEffect(() => {
    const env = process.env.NODE_ENV;
    if (env === 'development') return;

    const handleUnhandledRejection = (e) => {
      e.preventDefault();
      window.location.href = `${PATH.ERROR}`;
    };
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection,
      );
    };
  }, []);

  return (
    <BrowserRouter>
      {/* <Sentry.ErrorBoundary fallback={(error) => <ErrorPage error={error} />}> */}
      <FeatureMaintenance>
        <SentryRoutes>
          <Route
            path={`/p/:parkinglotCode/c/:serialCode`}
            element={<SerialCodeEntryPage />}
          />
          <Route path={PATH.POIS} element={<PoiEntryPage />} />
          <Route
            path={PATH.SEARCH_PLATE_NUMBER}
            element={<SearchPlateNumberPage />}
          />
          <Route
            path={PATH.SELECT_PLATE_NUMBER}
            element={<SelectPlateNumberPage />}
          />
          <Route path={PATH.PAYMENT} element={<PaymentPage />} />
          <Route path={PATH.AUTH} element={<AuthPage />} />
          <Route path={PATH.SUCCESS} element={<SuccessPage />} />
          <Route path={PATH.LEAVE} element={<LeavePage />} />
          <Route path={PATH.ERROR} element={<ErrorPage />} />
          <Route path={`${PATH.PROMOTION}/*`} element={<PromotionPage />} />
          <Route path="/" element={<ErrorPage />} />
        </SentryRoutes>
      </FeatureMaintenance>
      {/* </Sentry.ErrorBoundary> */}
    </BrowserRouter>
  );
};

export default App;
